import { useState, lazy, Suspense, useCallback } from 'react';
import { Send, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { useTemplates, useSaveTemplate } from '@/api';
import { ErrorBoundary } from 'react-error-boundary';
import toast from 'react-hot-toast';
import styles from './EmailOutreach.module.css';

const ComposeTab = lazy(() =>
  import('@/components/outreach/ComposeTab').then((m) => ({ default: m.ComposeTab })),
);
const TemplatesTab = lazy(() =>
  import('@/components/outreach/TemplatesTab').then((m) => ({ default: m.TemplatesTab })),
);

type Tab = 'compose' | 'templates';

export const EmailOutreach = () => {
  const [activeTab, setActiveTab] = useState<Tab>('compose');

  const { data: templates = [], isLoading: templatesLoading } = useTemplates();
  const saveTemplateMutation = useSaveTemplate();

  const handleSaveTemplate = useCallback(
    async (template: any) => {
      try {
        await saveTemplateMutation.mutateAsync(template);
        toast.success('Template saved!');
      } catch {
        toast.error('Failed to save template');
      }
    },
    [saveTemplateMutation],
  );

  const tabs: { key: Tab; label: string; icon: typeof Send }[] = [
    { key: 'compose', label: 'Compose', icon: Send },
    { key: 'templates', label: 'Templates', icon: FileText },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.headerTitle}>Email Outreach</h1>
            <p className={styles.headerDesc}>Manage templates and send email campaigns.</p>
          </div>
        </div>

        {/* Tab bar */}
        <div>
          <div className={styles.tabBar}>
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`${styles.tabBtn} ${activeTab === key ? styles.tabBtnActive : ''}`}
              >
                <Icon className={styles.tabIcon} />
                {label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {templatesLoading ? (
            <div className={styles.loadingState}>
              <Loader2 className={styles.spinner} />
              <span className={styles.loadingText}>Loading...</span>
            </div>
          ) : (
            <ErrorBoundary
              fallback={
                <div className={styles.loadingState}>
                  <AlertTriangle size={24} />
                  <span className={styles.loadingText}>
                    Something went wrong. Please refresh the page.
                  </span>
                </div>
              }
            >
              <Suspense
                fallback={
                  <div className={styles.loadingState}>
                    <Loader2 className={styles.spinner} />
                    <span className={styles.loadingText}>Loading...</span>
                  </div>
                }
              >
                {activeTab === 'compose' && <ComposeTab templates={templates} />}
                {activeTab === 'templates' && (
                  <TemplatesTab templates={templates} onSave={handleSaveTemplate} />
                )}
              </Suspense>
            </ErrorBoundary>
          )}
        </div>
      </div>
    </div>
  );
};
